import React from "react";
import { Box, Button, LinearProgress, Typography } from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import CloudSyncRoundedIcon from "@mui/icons-material/CloudSyncRounded";
import WifiOffRoundedIcon from "@mui/icons-material/WifiOffRounded";
import { getAxiosBaseUrl } from "../../api/axios";
import {
  SERVICE_UNAVAILABLE_EVENT,
  healthUrlFromApiBase,
} from "../../utils/serviceAvailability";

const RETRY_MS = 8000;

export default function ServiceUpdateBoundary({ children }) {
  const [unavailable, setUnavailable] = React.useState(false);
  const [checking, setChecking] = React.useState(false);
  const [offline, setOffline] = React.useState(
    () => typeof navigator !== "undefined" && navigator.onLine === false,
  );

  const checkBackend = React.useCallback(async () => {
    if (checking) return false;
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      setOffline(true);
      return false;
    }
    setChecking(true);
    try {
      const response = await fetch(healthUrlFromApiBase(getAxiosBaseUrl()), {
        method: "GET",
        cache: "no-store",
        credentials: "omit",
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return false;
      window.location.reload();
      return true;
    } catch {
      return false;
    } finally {
      setChecking(false);
    }
  }, [checking]);

  React.useEffect(() => {
    const onUnavailable = () => {
      setOffline(typeof navigator !== "undefined" && navigator.onLine === false);
      setUnavailable(true);
    };
    window.addEventListener(SERVICE_UNAVAILABLE_EVENT, onUnavailable);
    return () => window.removeEventListener(SERVICE_UNAVAILABLE_EVENT, onUnavailable);
  }, []);

  React.useEffect(() => {
    if (!unavailable) return undefined;
    const interval = window.setInterval(checkBackend, RETRY_MS);
    const onOnline = () => {
      setOffline(false);
      checkBackend();
    };
    const onOffline = () => setOffline(true);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [checkBackend, unavailable]);

  if (!unavailable) return children;

  return (
    <Box
      role="alert"
      aria-live="assertive"
      sx={{ minHeight: "100vh", display: "grid", placeItems: "center", px: 3, bgcolor: "#fff" }}
    >
      <Box sx={{ width: "min(560px, 92vw)", textAlign: "center" }}>
        <Box
          sx={{
            mx: "auto",
            width: 64,
            height: 64,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(243,90,58,.10)",
            color: "#f35a3a",
          }}
        >
          {offline ? <WifiOffRoundedIcon sx={{ fontSize: 34 }} /> : <CloudSyncRoundedIcon sx={{ fontSize: 34 }} />}
        </Box>
        <Typography variant="h4" sx={{ mt: 2.2, fontWeight: 950, color: "#19242f" }}>
          {offline ? "Parece que estás sin conexión" : "Estamos reconectando con WebProfit"}
        </Typography>
        <Typography sx={{ mt: 1.2, color: "#5f6b76", lineHeight: 1.7, fontSize: 16 }}>
          {offline
            ? "No pudimos acceder a Internet desde este dispositivo. Revisá tu Wi‑Fi o datos móviles; cuando vuelva la conexión, WebProfit se recuperará automáticamente."
            : "La conexión con WebProfit se interrumpió por un momento. Puede deberse a una actualización breve del servicio o a una interrupción de red. Estamos reintentando automáticamente."}
        </Typography>
        <LinearProgress
          sx={{
            mt: 3,
            height: 6,
            borderRadius: 999,
            bgcolor: "rgba(243,90,58,.12)",
            "& .MuiLinearProgress-bar": { bgcolor: "#f35a3a", borderRadius: 999 },
          }}
        />
        <Typography sx={{ mt: 1, color: "#8a949e", fontSize: 12.5 }}>
          {offline
            ? "No necesitás recargar: detectamos automáticamente cuando vuelve Internet."
            : "Normalmente se resuelve en unos instantes y la página vuelve sola."}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshRoundedIcon />}
          onClick={checkBackend}
          disabled={checking || offline}
          sx={{ mt: 2.3, textTransform: "none", fontWeight: 800 }}
        >
          {checking ? "Comprobando…" : offline ? "Esperando conexión…" : "Reintentar conexión"}
        </Button>
      </Box>
    </Box>
  );
}
